<template>
  <!-- Note that we have issues going to fullscreen on mobile browsers -->
  <video-overlay
    ref="overlay"
    :paused.sync="paused"
    :t.sync="t"
    :duration="duration"
    :loading="loadStatus.pending || playStatus.pending || (didInitGainNodes && missingData) || !scale || loadMediaBoundaryStatus.pending"
    :disabled="loadStatus.pending || needsReload || !scale"
    :allow-fullscreen="!IS_MOBILE"
  >

    <template v-slot:header>
      <slot></slot>
    </template>

    <!-- For now, I decided it's misleading to show this loader -->
    <!--
    <template v-slot:fixed-footer>
      <v-progress-linear v-if="!needsReload && tLoaded != duration" :value="100 * tLoaded / duration" />
    </template>
    -->

    <v-responsive-bounds
      class="mx-auto overflow-hidden black"
      :aspect-ratio="video.w / video.h"
      v-slot="{ bounds }"
      @updated="bounds => scale = SUPER_SAMPLE * bounds.w / video.w"
    >

      <!-- This section goes along with "Approach #2" in the load() function and is experimental / incomplete -->
      <!--
      <div class="red" style="transform: scale(.1);">
        <template v-for="media in attachedReuse">
          <audio
            v-if="media.type == 'AUDIO'"
            ref="audio-media"
            width="10" preload="auto" crossorigin="anonymous" loop=""
            :src="media.defaultMedia.src"
            @canplay="e => (e.target.src != media.defaultMedia.src) && enqueueUpdate()"
            @canplaythrough="e => (e.target.src != media.defaultMedia.src) && enqueueUpdate()"
          />
          <video
            v-else
            ref="audio-media"
            width="10" preload="auto" crossorigin="anonymous" loop="" playsinline="playsinline"
            :src="media.defaultMedia.src"
            @canplay="e => (e.target.src != media.defaultMedia.src) && enqueueUpdate()"
            @canplaythrough="e => (e.target.src != media.defaultMedia.src) && enqueueUpdate()"
          />
        </template>
      </div>
      -->

      <canvas
        ref="canvas"
        v-show="!needsReload"
        style="transform-origin: top left;"
        v-bind:style="{ transform: `scale(${1 / SUPER_SAMPLE})` }"
        :width="SUPER_SAMPLE * bounds.w" :height="SUPER_SAMPLE * bounds.h"
      />

      <div
        v-if="needsReload"
        class="absolute-fill d-flex flex-column justify-center align-center"
        style="z-index: 1;"
      >
        <div class="white--text overline text-center">
          <span v-if="loadStatus.pending">Optimizing…</span>
          <span v-else>Project updated</span>
        </div>
        <v-btn :loading="loadStatus.pending" @click.stop="load" color="primary">
          Reload<v-icon class="ml-2" v-html="mdiRefresh" />
        </v-btn>
      </div>
    </v-responsive-bounds>
  </video-overlay>
</template>

<script>
import VResponsiveBounds from '@/components/VResponsiveBounds.vue';
import CropMedia from '@/components/CropMedia.vue';
import VideoOverlay from '@/components/VideoOverlay.vue';
import { buildTextImgSrc } from '@/utils/render-text.js';
import { getOptimizedSrc, getOptimizedBlobSrc } from '@/utils/video.js';
import { mdiAlert, mdiClose, mdiRefresh } from '@mdi/js';


// The larger you set the threshold for PRELOAD, the more reusable media elements you might need to have, which would
// ironically hurt performance, as the initial call to 'play' would struggle.
const PRELOAD = 10;

const FRAMERATE = 1 / 24;

// Scale up our canvas by this amount to do the painting to address anti-aliasing issues (particularly visible on iOS)
const SUPER_SAMPLE = 2;

// If we're dropping frames for at least this many seconds, auto-pause the video
const DROP_TOLERANCE = 3;

const LOAD_RETRY_DELAY_MS = 1500;

const AUDIO_MEDIA_TYPES = new Set([ 'AUDIO', 'AUDIO_VIDEO' ]);
const DYNAMIC_MEDIA_TYPES = new Set([ 'AUDIO', 'AUDIO_VIDEO', 'VIDEO' ]);

// We need to provide default empty sources for the reusable media containers
// Here's how you created the empty audio / video resources:
// ffmpeg -f lavfi -i anullsrc=channel_layout=stereo:sample_rate=44100 -t 1 empty.mp3
// ffmpeg -f lavfi -i color=c=black:s=160x120 -c:v libx264 -tune stillimage -pix_fmt yuv420p -t 1 empty.mp4
const loadAsObjectURL = src => fetch(src).then(r => r.blob()).then(URL.createObjectURL);
const DEFAULT_SRCS = { AUDIO: loadAsObjectURL('/empty.mp3'), AUDIO_VIDEO: loadAsObjectURL('/empty.mp4') };


function buildNonOverlappingGroups(media, buffer=PRELOAD) {
  // Given an input array of media element definitions, returns a new array where each element of the output array is
  // itself an array containing the IDs of all the media elements whose automation timestamps do not overlap. The
  // output array should be as short as possible.
  // [
  //   { id: 'A', automation: [ { t: 0 }, { t: 10 } ] },
  //   { id: 'B', automation: [ { t: 1 }, { t: 2 } ] },
  //   { id: 'C', automation: [ { t: 5 }, { t: 8 } ] },
  //   { id: 'D', automation: [ { t: 4 }, { t: 9 } ] },
  //   { id: 'E', automation: [ { t: 3 }, { t: 6 } ] },
  //   { id: 'F', automation: [ { t: 7 }, { t: 9 } ] }
  // ]
  // -> [ [ 'A' ], [ 'B', 'E' ], [ 'D' ], [ 'C', 'F' ] ] // 4 groups
  // -> [ [ 'A' ], [ 'B', 'E', 'F' ], [ 'D' ], [ 'C' ] ] // 4 groups (also valid)

  const boundaries = [];
  for (const m of media) {
    boundaries.push({ t: m.automation[0].t - buffer, id: m.id, start: true });
    boundaries.push({ t: m.automation[m.automation.length - 1].t, id: m.id, start: false });
  }
  boundaries.sort((a, b) => a.t - b.t);

  const slotsById = {};
  const available = [];
  for (const boundary of boundaries) {
    if (boundary.start) {
      const slot = available.pop() || [];
      slot.push(boundary.id);
      slotsById[boundary.id] = slot;
    } else {
      available.push(slotsById[boundary.id]);
    }
  }
  return available;
}

async function getOptimizedMediaSrc(media, compressed=false) {
  if (media.type == 'TEXT')
    // In this case, we already return a cached, local object URL
    return await buildTextImgSrc(media);
  if (media.src.startsWith('blob:'))
    // This object is already stored locally
    return media.src;
  let src = media.src;
  if (media.type == 'AUDIO_VIDEO' || media.type == 'VIDEO')
    src = await getOptimizedSrc(media.src, compressed ? '.m.mp4' : '.mp4');
  return src;
}

function getAutomationBoundaries(media, t) {
  let min, max;
  for (const a of media.automation || []) {
    if (a.t <= t) {
      min = a;
      continue;
    }
    if (a.t >= t) {
      max = a;
      break;
    }
  }
  return [ min, max ];
}

function getFadeInOutPct(media, t) {
  const t0 = media.automation[0].t;
  const t1 = media.automation[media.automation.length - 1].t;
  if (t < t0 || t > t1)
    return 0;
  const fadeInOpacity = media.fade_in ? (t - t0) / media.fade_in : 1;
  const fadeOutOpacity = media.fade_out ? (t1 - t) / media.fade_out : 1;
  return Math.max(0, Math.min(1, fadeInOpacity, fadeOutOpacity));
}

function getAutomation(media, t, scale) {
  const [ min, max ] = getAutomationBoundaries(media, t);
  if (!min || !max)
    return null;
  const fade = getFadeInOutPct(media, t, min.t, max.t);
  const pct = (t - min.t) / (max.t - min.t);
  const output = {};
  if (media.hasVideo) {
    output.x = scale * (min.x + pct * (max.x - min.x));
    output.y = scale * (min.y + pct * (max.y - min.y));
    output.w = scale * (min.w + pct * (max.w - min.w));
    output.h = scale * (min.h + pct * (max.h - min.h));
    output.r = min.r + pct * (max.r - min.r);
    output.o = fade;
  }
  if (media.hasAudio)
    output.v = fade * (min.v + pct * (max.v - min.v));
  return output;
}

function buildAttachedMediaBoundaries(media) {
  // Returns an array of tuples [ t, { id_1, ... } ] sorted in ascending order by 't' where the second item in each
  // tuple is a set of media IDs that should be attached starting at the specified timestamp 't' all the way up to the
  // next entry in the list. Importantly, those sets preserve the order of the media layers.
  // For example, the following structure describes where the background element '0' should be attached for any 't'
  // where 0 <= t < 2.04, but the element '1' should only be attached for 0 <= t < 1
  // [
  //   [ 0, [ 0, 1 ] ],
  //   [ 1.04, [ 0 ] ],
  //   [ 2.04, [] ]
  // ]
  // This allows us to quickly look up the attached media elements for any given timestamp with O(1) complexity when
  // playing or scrubbing a video, since we know that 't' increases / decreases incrementally. This is more efficient
  // than filtering through all media elements on every tick.
  const boundaries = { 0: [ 0, new Set() ] };
  for (const m of media) {
    m.t0 = Math.max(0, m.automation[0].t - PRELOAD);
    m.t1 = m.automation[m.automation.length - 1].t + FRAMERATE;
    boundaries[m.t0] = [ m.t0, new Set() ];
    boundaries[m.t1] = [ m.t1, new Set() ];
  }

  // Now make sure we know the 'next' index of each boundary, allowing us to traverse our 'boundaries' dictionary as a
  // sorted linked list
  const sorted = Object.values(boundaries).sort((a, b) => a[0] - b[0]);
  for (let i = 0; i < sorted.length - 1; i++)
    sorted[i].next = sorted[i + 1][0];

  for (const m of media) {
    let cur = boundaries[m.t0];
    while (cur && cur[0] < m.t1) {
      cur[1].add(m.id);
      cur = boundaries[cur.next]
    }
  }

  for (const boundary of sorted)
    delete boundary.next;

  return sorted;
}

function findBoundaryI(boundaries, t, last={ t: 0, i: 0 }) {
  // Uses the 'last' found boundary as a hint to quickly look up the index of the relevant attached media IDs
  if (t >= last.t) {
    for (let i = last.i; i < boundaries.length - 1; i++)
      if (boundaries[i + 1][0] >= t)
        return i;
    return boundaries.length - 1;
  } else {
    for (let i = last.i; i > 0; i--)
      if (boundaries[i - 1][0] < t)
        return i - 1;
    return 0;
  }
}

export default {
  name: 'video-preview-player',

  components: {
    CropMedia,
    VideoOverlay,
    VResponsiveBounds
  },

  props: [ 'video' ],

  data () {
    return {
      needsReload: false,
      compressed: false,
      paused: true,
      t: 0,
      tLoaded: 0,
      missingData: true,
      scale: 0,
      media: [],
      attached: {}, // media ID -> media element
      attachedReuse: [], // reuse ID -> { type: AUDIO | AUDIO_VIDEO, media, defaultMedia, gain: null, el }
      boundaries: [ [ 0, new Set() ] ],
      loadStatus: this.$track('load'),
      playStatus: this.$track('play'),
      loadMediaBoundaryStatus: this.$track('loadMediaBoundary')
    }
  },

  computed: {
    duration() {
      return Math.max(...this.video.media.map(x => x.automation[x.automation.length - 1].t));
    }
  },

  created() {
    Object.assign(this, { SUPER_SAMPLE, mdiAlert, mdiClose, mdiRefresh });
  },

  beforeDestroy() {
    this.cleanUp();
  },

  methods: {
    cleanUp() {
      this.paused = true;
      clearInterval(this.tick);
      cancelAnimationFrame(this.anim);
      for (const el of [ ...Object.values(this.attached), ...this.attachedReuse.map(x => x.el) ])
        el.pause && el.pause();
        // el.removeAttribute('src');
        // el.load();
      this.attached = {};
      this.attachedReuse = [];
    },

    initGainNodes() {
      // This method should only be triggered by a user event so we have permission to play any media elements in here.
      // Make sure that there are no 'await' calls anywhere between the event handler and calling this method,
      // otherwise iOS will not treat it as coming from a user action.

      // See here for more information on audio contexts:
      // https://mdn.github.io/webaudio-examples/media-source-buffer/
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      for (const c of this.attachedReuse) {
        const source = ctx.createMediaElementSource(c.el);
        const node = ctx.createGain();
        source.connect(node).connect(ctx.destination);
        node.gain.value = 0;
        c.gain = node;

        // Note that this call may generate an error due to the 'src' getting swapped out on the media element later
        // https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
        c.el.play().catch(e => null);
      }

      this.didInitGainNodes = true;
    },

    async play() {
      this.$emit('play-video');

      if (this.t >= this.duration)
        this.t = 0;

      if (!this.didInitGainNodes) {
        this.initGainNodes();

        // This is the first time we're playing, and it's a lot of work to init the gain nodes. Rather than just trying
        // to launch into the update sequence, let's just call it once to attach the right nodes, then make sure all of
        // our media is in a ready state before we actually start. I don't like polling, but given the complexity of
        // how we init and reuse nodes, it seems like the simplest approach...
        await this.update();
        while ([ ...Object.values(this.attached), ...this.attachedReuse.map(x => x.el) ].some(x => x.readyState < 3))
          await new Promise(r => setTimeout(r, 100));
      }

      const absPlayStart = Date.now() / 1000 - this.t;
      this.tick = setInterval(
        () => this.t = Math.min(this.duration, Date.now() / 1000 - absPlayStart),
        FRAMERATE * 1000
      );
    },

    async pause() {
      clearInterval(this.tick);
      this.enqueueUpdate();
    },

    buildTemporaryMediaEl(m) {
      if (this.attached[m.id])
        return this.attached[m.id];
      let el;
      switch (m.type) {
        case 'IMG':
        case 'TEXT':
          el = document.createElement('img');
          el.addEventListener('load', this.enqueueUpdate);
          break;
        case 'VIDEO':
          el = document.createElement('video');
          el.addEventListener('canplay', this.enqueueUpdate);
          el.addEventListener('canplaythrough', this.enqueueUpdate);
          // This attribute is finicky on iOS - it can only be set like this apparently
          el.setAttribute('playsinline', 'playsinline');
          el.preload = 'auto';
          el.muted = true;
          el.loop = true;
          break;
      }
      el.crossOrigin = 'anonymous';
      return (this.attached[m.id] = el);
    },

    getEl(m) {
      return m.reuse == -1 ? this.buildTemporaryMediaEl(m) : this.attachedReuse[m.reuse].el;
    },

    async loadMedia(m) {
      // Post-conditions: the element corresponding to this media exists and has the correct source
      // TODO: you could do this in a web worker (except for text rendering)
      // Mutates the 'optimized' property of the target 'm' and loads the source into the browser's cache
      const el = this.getEl(m);
      if (!m.getSrc)
        m.getSrc = getOptimizedMediaSrc(m, this.compressed);
      if (el._mediaId == m.id)
        return;
      if (el._mediaId != undefined) {
        // Some other media wants to use this element - only let that happen if the time is right
        const a = m.automation;
        if (!((a[0].t - PRELOAD <= this.t) && (this.t <= a[a.length - 1].t)))
          // This media should not be loaded into this reusable element yet
          return;
      }
      el._mediaId = m.id;
      el.src = await m.getSrc;
      if (m.isDynamic)
        el.load();
    },

    getBoundaryI() {
      // This is a complex approach that takes advantage of our pre-loaded 'boundaries' - that array is sorted by
      // timestamp, and each entry contains the attached media for that timestamp that should stay attached at least
      // until the next entry in the array. A more naive approach might be to simply filter through all the media to
      // find the ones that should currently be attached, but that may not scale with large amounts of media.
      const i = findBoundaryI(this.boundaries, this.t, this.last);
      this.last = { t: this.boundaries[i][0], i };
      return i;
    },

    async loadMediaBoundary(boundaryI) {
      const boundary = this.boundaries[boundaryI];
      await Promise.all(Array.from(boundary[1]).map(id => this.loadMedia(this.media[id])));

      const next = this.boundaries[boundaryI + 1];

      // Note that 'tLoaded' actually means FULLY loaded - we may be able to play through from earlier timestamps since
      // we can stream partially loaded files
      this.tLoaded = Math.max(this.tLoaded, Math.min(next ? next[0] : this.duration, this.duration));

      // Only load the media from the next boundary if our playhead is behind it and we're within PRELOAD seconds of it
      if (next && (this.t < next[0]) && (this.t + PRELOAD > next[0]))
        this.loadMediaBoundary(boundaryI + 1);
    },

    initCanvasCtx() {
      const cnv = this.$refs.canvas;
      if (!cnv)
        return false;
      if (!this.buffer || this.buffer.width != cnv.width || this.buffer.height != cnv.height) {
        // The 'ctx' that we actually do all our drawing on is the buffer - we'll swap that out to the main context
        // once we're sure we didn't drop any frames, otherwise we could see the various elements "flicker"
        this.buffer = document.createElement('canvas');
        this.buffer.width = cnv.width;
        this.buffer.height = cnv.height;
        this.ctx = this.buffer.getContext('2d');
        this.ctxMain = cnv.getContext('2d');
      }
      return true;
    },

    async enqueueUpdate() {
      // Note that any underlying audio will keep playing even if we drop updates
      if (this.updatePending) {
        // We're still processing a previous call
        this.needsUpdate = true;
        return;
      }
      this.needsUpdate = false;
      this.updatePending = true;
      try {
        await this.update();
      } catch (e) {
        console.error('Error updating', e);
      } finally {
        this.updatePending = false;
        if (this.needsUpdate)
          this.enqueueUpdate();
      }
    },

    async update() {
      if (!this.initCanvasCtx())
        return;
      this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);

      if (!this.paused && this.t >= this.duration) {
        this.paused = true;
        this.t = this.duration;
        return;
      }

      const boundaryI = this.getBoundaryI();
      await this.loadMediaBoundary(boundaryI);

      let missingData = false;
      const newAttachedIds = this.boundaries[boundaryI][1];
      for (const id of newAttachedIds) {
        const m = this.media[id];
        const a = getAutomation(m, this.t, this.scale);
        const el = this.getEl(m);

        // Every time we update, check the ready state of all the media and show the loading indicator if anything is
        // not ready. Note that we need to make sure that the all media elements call this function when their ready
        // state changes (including images).
        if (a && (m.isDynamic ? el.readyState <= 1 : !el.complete))
          missingData = true;

        if (m.isDynamic) {
          // We always need to have a volume present for any dynamic media with audio
          m.a = a || { v: 0 };
          if (m.reuse == -1)
            this.enqueueUpdatePlayback(el);
        }

        // Actually draw the visual elements on the canvas
        if (a && this.ctx && m.hasVideo && !missingData) {
          let w, h, offsetX = 0, offsetY = 0, crop = [];
          if (m.crop) {
            if (m.type == 'IMG') {
              w = el.naturalWidth;
              h = el.naturalHeight;
            } else {
              w = el.videoWidth;
              h = el.videoHeight;
            }
            const { bottom, left, right, top } = m.crop;
            crop = [ left * w, top * h, w - w * (left + right), h - h * (top + bottom) ];
          }

          this.ctx.save();
          this.ctx.globalAlpha = a.o;
          this.ctx.translate(a.x, a.y);
          this.ctx.rotate((a.r || 0) * Math.PI / 180);
          this.ctx.rotate((m.r || 0) * Math.PI / 180);

          const bounds = ((m.r || 0) % 180 != 0) ? [ -a.h / 2, -a.w / 2, a.h, a.w ] : [ -a.w / 2, -a.h / 2, a.w, a.h ];
          if (m.isDynamic && a.o == 1) {
            // We don't support transparency for videos - a black background box (instead of transparent) looks better
            // if there are loading glitches, and it makes it easier to determine the letterbox if we just draw a black
            // rectangle first. Note that we're also careful not to add this if there's any opacity in the automation.
            this.ctx.beginPath();
            this.ctx.rect(...bounds);
            this.ctx.fill();
            this.ctx.closePath();
          }

          // Note that we use a polyfill to fully support 'drawImage' in Safari
          this.ctx.drawImage(el, ...crop, ...bounds);
          this.ctx.restore();
        }
      }

      // TODO: to keep memory under control, unload previous media elements that are no longer used (we don't need to
      // do this every frame)
      // if (!this.paused) {
      //   for (const id in this.attached) {
      //     const m = this.media[Number(id)];
      //     const t1 = m.automation[m.automation.length - 1].t;
      //     if (t1 < this.t) {
      //       this.attached[id].remove();
      //       delete this.attached[id];
      //     }
      //   }
      // }

      // Make sure we always update all the dynamic media containers - there can occasionally be issues with looped
      // resources continuing to play
      for (const c of this.attachedReuse)
        this.enqueueUpdatePlayback(c.el);

      if (missingData) {
        this.tDrop = this.tDrop || this.t;
        if (this.t - this.tDrop > DROP_TOLERANCE) {
          if (!this.paused) {
            if (this.$refs.overlay.isFullscreen)
              this.$refs.overlay.toggleFullscreen();
            this.$emit('slow-playback');
          }
          this.paused = true;
          this.tDrop = 0;
          this.missingData = true;
        }
      } else {
        this.tDrop = 0;
        this.missingData = false;

        // Swap out our buffer since we know it's complete - we use this buffer pattern because it's better to drop
        // entire frames than to flicker part of them (slightly less efficient though)
        this.ctxMain.clearRect(0, 0, this.buffer.width, this.buffer.height);
        this.ctxMain.drawImage(this.buffer, 0, 0);
      }
    },

    enqueueUpdatePlayback(el) {
      if (el._updatePlaybackPending) {
        // We're still processing a previous call
        el._needsUpdatePlayback = true;
        return;
      }
      el._needsUpdatePlayback = false;
      el._updatePlaybackPending = true;
      try {
        this.updatePlayback(el);
      } catch (e) {
        console.error('Error updating playback', el._mediaId, e);
      } finally {
        el._updatePlaybackPending = false;
        if (el._needsUpdatePlayback)
          this.enqueueUpdatePlayback(el);
      }
    },

    updatePlayback(el) {
      const m = this.media[el._mediaId];
      if (!m) {
        // This must be the default media (which should never be playing)
        if (!el.paused)
          el.pause();
        return;
      }
      const t0 = m.automation[0].t;
      const t1 = m.automation[m.automation.length - 1].t;
      const duration = m.duration || (t1 - t0);
      const tOffset = m.t_offset || 0;
      const tMedia = Math.min(Math.max(0, (this.t - t0) % duration + tOffset), tOffset + duration);

      if (this.paused || this.t < t0 || this.t > t1 - FRAMERATE) {
        if (!el.paused)
          el.pause();
        if (el.readyState == 0)
          el.load(); // This will eventually trigger an update call itself
        else if (Math.abs(el.currentTime - tMedia) > FRAMERATE)
          el.currentTime = tMedia;
        return;
      }

      // Note that some of our dynamic media elements may not actually use a reusable container
      const c = this.attachedReuse[m.reuse];
      if (m.a && !isNaN(m.a.v) && c && c.gain)
        // Multiply the automation volume by the intrinsic volume adjustment
        c.gain.gain.value = (m.v == null ? 1 : m.v) * m.a.v;

      if (el.paused && !this.paused) {
        if (el.readyState == 0)
          el.load();
        else if (Math.abs(el.currentTime - tMedia) > FRAMERATE)
          el.currentTime = tMedia;
        else if (this.t >= t0 && this.t < t1)
          el.play().catch(e => null);
      }
    },

    async load() {
      // We use the media ID to identify these elements, and sometimes the same media ID might have a different source
      // in successive reloads if this component stays active, so we make sure to remove all the existing elements.
      this.cleanUp();

      this.didLoad = true;
      this.didInitGainNodes = false;

      const media = this.video.media.map(
        (x, id) => ({
          ...x,
          id, // Note that we override the media's native 'id' with this much shorter and simpler numeric identifier
          reuse: -1, // Identifies the media container to reuse
          hasAudio: AUDIO_MEDIA_TYPES.has(x.type),
          hasVideo: x.type != 'AUDIO',
          isDynamic: DYNAMIC_MEDIA_TYPES.has(x.type)
        })
      );

      const mediaContainers = [];
      for (const type of AUDIO_MEDIA_TYPES) {
        const src = await DEFAULT_SRCS[type];
        const groups = buildNonOverlappingGroups(media.filter(x => x.hasAudio && x.type == type));
        for (const group of groups) {
          const i = mediaContainers.length;
          for (const j of group)
            media[j].reuse = i;

          // Approach #1: do not attach the elements to the DOM
          const defaultMedia = { id: -1 - i, src };
          const el = document.createElement(type == 'AUDIO' ? 'audio' : 'video');
          const update = e => (e.target.src != src) && this.enqueueUpdate();
          el.addEventListener('canplay', update);
          el.addEventListener('canplaythrough', update);
          el.setAttribute('playsinline', 'playsinline');
          el.preload = 'auto';
          el.crossOrigin = 'anonymous';
          el.loop = '';
          el.src = src;
          mediaContainers.push({ key: 'c' + i, type, defaultMedia, gain: null, el });

          // Approach #2: create elements in the DOM in case they actually need to be visible and in the viewport to
          // auto-play - https://stackoverflow.com/questions/34838052/can-html5-video-preload-be-forced-on-ios-devices
          // const parent = this;
          // mediaContainers.push({
          //   key: 'c' + i,
          //   type,
          //   defaultMedia: { id: -1 - i, src },
          //   gain: null,
          //   get el() { return parent.$refs['audio-media'][i] }
          // });
        }
      }

      this.media = media;
      this.attachedReuse = mediaContainers;
      this.boundaries = buildAttachedMediaBoundaries(media);
      this.enqueueUpdate();
      this.needsReload = false;
    }
  },

  watch: {
    video: {
      immediate: true,
      deep: true,
      handler() {
        this.paused = true;
        this.t = 0;
        this.tLoaded = 0;
        if (this.didLoad)
          this.needsReload = true;
        else
          this.load();
      }
    },

    t() {
      this.enqueueUpdate();
    },

    paused(x) {
      x ? this.pause() : this.play();
    },

    scale() {
      if (this.didLoad)
        this.enqueueUpdate();
    }

  }
}
</script>
